import Image from 'next/image';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import useWindowSize from 'hooks/useWindowSize';
import { loginLink } from 'helpers/page';
import React from 'react';

const check = '/static/images/pro-bidder-membership/Check.png';
const xMark = '/static/images/pro-bidder-membership/Xmark.png';

interface Membership {
    imageSrc: string;
    title: string;
    color: string;
    monthlyFee: string;
    buyerPremium: string;
    premiumMinimum: string;
    bidRadius: string;
    maxBidLimit: string;
    maxBidLimitDisclaimer?: string;
    highBidderLimit: string;
    highBidderLimitDisclaimer?: string;
    authorizedForPickup: string;
    dedicatedEmailSupport: boolean;
    dedicatedPhoneSupport: boolean;
    suspensionReview: boolean;
    backupOffers: boolean;
    maxBackupBidderOffersDisclaimer?: string;
    buttonLink: string;
    buttonText: string;
}

interface MembershipComparisonProps {
    memberships: Membership[];
    authed?: boolean;
    onClick?: () => void;
    disableBtns?: boolean;
}

const MembershipComparison: React.FC<MembershipComparisonProps> = ({
    memberships,
    authed = false,
    onClick = () => {},
    disableBtns = false,
}) => {
    const width = useWindowSize().width;

    const RenderCompareBox: React.FC<{ src: string; text: string; color: string }> = ({ src, text, color }) => {
        return (
            <div style={{ backgroundColor: color }} className="compare-box">
                <Image src={src} width={100} height={100} alt="Compare Box" />
                <p>{text}</p>
            </div>
        );
    };

    const TableValue: React.FC<{ text: string; color: string; yesNo?: boolean; disclaimer?: string }> = ({
        text,
        color,
        yesNo = false,
        disclaimer,
    }) => {
        return (
            <div className="table-value" style={{ backgroundColor: color }}>
                {yesNo ? (
                    <p className={text === 'Yes' ? 'text-blue' : 'text-orange'}>{text}</p>
                ) : (
                    <p>{text}</p>
                )}
                {disclaimer && <p className="disclaimer">{disclaimer}</p>}
            </div>
        );
    };

    const TableTooltip: React.FC<{ title: string; disclaimer?: string; text?: string }> = ({
        title,
        disclaimer,
        text,
    }) => {
        return (
            <span className="tooltip-blue">
                {title}
                {text && (
                    <OverlayTrigger placement="top" overlay={<Tooltip id="soft-close-tooltip">{text}</Tooltip>}>
                        <i className="fa fa-question-circle marginL5" aria-hidden="true" />
                    </OverlayTrigger>
                )}
                {disclaimer && <p className="disclaimer">{disclaimer}</p>}
            </span>
        );
    };

    const BtnLink: React.FC<{ label: string; to: string }> = ({ label, to }) => {
        return (
            <div className="table-btn-container">
                <button
                    className="btn btn-blue tier-card-btn-primary"
                    onClick={() => onClick()}
                    disabled={disableBtns}
                >
                    {label}
                </button>
            </div>
        );
    };

    return width < 816 ? (
        <>
            {memberships.map((membership, index) => (
                <div key={index}>
                    <div className="table-comparison-headers">
                        <div className="features-box">
                            <p>Membership Features</p>
                        </div>
                        <RenderCompareBox src={membership.imageSrc} text={membership.title} color={membership.color} />
                    </div>
                    <div className="comparison-table">
                        <div className="comparison-row">
                            <div className="pro-membership-pro-membership-table-title">
                                <p>Monthly Fee</p>
                            </div>
                            <TableValue text={membership.monthlyFee} color="#ededed" />
                        </div>
                        <div className="comparison-row">
                            <div className="table-title-white">
                                <p>Buyer Premium</p>
                            </div>
                            <TableValue text={membership.buyerPremium} color="#F5F5F5" />
                        </div>
                        <div className="comparison-row">
                            <div className="pro-membership-table-title">
                                <p>Premium Minimum</p>
                            </div>
                            <TableValue text={membership.premiumMinimum} color="#ededed" />
                        </div>
                        <div className="benefits-title">
                            <p>Membership Benefits</p>
                        </div>
                        <div className="comparison-row">
                            <div className="table-title-white table-title-light">
                                <TableTooltip
                                    title="Bid Radius"
                                    text="A geographical limit for auctions you can bid, based on your zip code"
                                />
                            </div>
                            <TableValue text={membership.bidRadius} color="#F5F5F5" />
                        </div>
{/*                        <div className="comparison-row">
                            <div className="pro-membership-table-title table-title-light">
                                <TableTooltip
                                    title="Max Bid Limit"
                                    disclaimer="*based on auctions won"
                                    text="Buyers cannot bid over $1000 on auctions until they have successfully completed a specified number of previous auctions."
                                />
                            </div>
                            <TableValue text={membership.maxBidLimit} color="#ededed" disclaimer={membership.maxBidLimitDisclaimer} />
                        </div>*/}
                        <div className="comparison-row">
                            <div className="table-title-white table-title-light">
                                <TableTooltip
                                    title="High Bidder Limit"
                                    text="Buyers can be the highest bidder on up to 3 or 5 auctions at once, depending on membership, unless they have successfully completed a certain number of auctions."
                                    disclaimer="*based on auctions won"
                                />
                            </div>
                            <TableValue text={membership.highBidderLimit} color="#F5F5F5" disclaimer={membership.highBidderLimitDisclaimer} />
                        </div>
                        <div className="comparison-row">
                            <div className="pro-membership-table-title table-title-light">
                                <TableTooltip
                                    title="Authorized For Pickup"
                                    text="Designated individuals that can collect your winnings on your behalf. Only Pro membership accounts can have or be an Authorized Pickup Person."
                                />
                            </div>
                            <TableValue text={membership.authorizedForPickup} color={'#ededed'} />
                        </div>
                        <div className="comparison-row">
                            <div className="table-title-white table-title-light">
                                <TableTooltip
                                    title="Dedicated Email Support"
                                />
                            </div>
                            <TableValue text={membership.dedicatedEmailSupport ? 'Yes' : 'No'} color="#F5F5F5" yesNo={true} />
                        </div>
                        <div className="comparison-row">
                            <div className="pro-membership-table-title table-title-light">
                                <TableTooltip
                                    title="Dedicated Phone Support"
                                />
                            </div>
                            <TableValue text={membership.dedicatedPhoneSupport ? 'Yes' : 'No'} color="#ededed" yesNo={true} />
                        </div>
                        <div className="comparison-row">
                            <div className="table-title-white table-title-light">
                                <TableTooltip
                                    title="Suspension Review"
                                    text="Subject to the severity and number of incidents."
                                />
                            </div>
                            <TableValue text={membership.suspensionReview ? 'Yes' : 'No'} color="#F5F5F5" yesNo={true} />
                        </div>
                        <div className="comparison-row">
                            <div className="table-title-white table-title-light">
                                <TableTooltip
                                    title="Backup Bidder Offers"
                                    text="When the originating winning bidder fails to complete the unit process, buyers gain eligibility to these offers."
                                    disclaimer="* based on auctions won"
                                />
                            </div>
                            <TableValue text={membership.backupOffers ? 'Yes' : 'No'} color="#F5F5F5" yesNo={true} disclaimer={membership.maxBackupBidderOffersDisclaimer} />
                        </div>
                        <div className="table-btns">
                            <BtnLink
                                to={authed ? membership.buttonLink : loginLink({ redirect_to: '/pro-membership-upgrade' })}
                                label={membership.buttonText}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </>
    ) : (
        <>
            <div className="table-comparison-headers">
                <div className="features-box">
                    <p>Membership Features</p>
                </div>
                {memberships.map((membership, index) => (
                    <RenderCompareBox key={index} src={membership.imageSrc} text={membership.title} color={membership.color} />
                ))}
            </div>
            <div className="comparison-table">
                <div className="comparison-row">
                    <div className="pro-membership-table-title"><p>Monthly Fee</p></div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.monthlyFee} color="#ededed" />)}
                </div>
                <div className="comparison-row">
                    <div className="table-title-white"><p>Buyer Premium</p></div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.buyerPremium} color="#F5F5F5" />)}
                </div>
                <div className="comparison-row">
                    <div className="pro-membership-table-title"><p>Premium Minimum</p></div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.premiumMinimum} color="#ededed" />)}
                </div>
                <div className="benefits-title"><p>Membership Benefits</p></div>
                <div className="comparison-row">
                    <div className="table-title-white table-title-light">
                        <TableTooltip title="Bid Radius" text="A geographical limit for auctions you can bid, based on your zip code" />
                    </div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.bidRadius} color="#F5F5F5" />)}
                </div>
{/*                <div className="comparison-row">
                    <div className="pro-membership-table-title table-title-light">
                        <TableTooltip
                            title="Max Bid Limit"
                            text="Buyers cannot bid over $1000 on auctions until they have successfully completed a specified number of previous auctions."
                            disclaimer="*based on auctions won"
                        />
                    </div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.maxBidLimit} color="#ededed" disclaimer={membership?.maxBidLimitDisclaimer} />)}
                </div>*/}
                <div className="comparison-row">
                    <div className="table-title-white table-title-light">
                        <TableTooltip
                            title="High Bidder Limit"
                            text="Buyers can be the highest bidder on up to 3 or 5 auctions at once, depending on membership, unless they have successfully completed a certain number of auctions."
                            disclaimer="*based on auctions won"
                        />
                    </div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.highBidderLimit} color="#F5F5F5" disclaimer={membership?.highBidderLimitDisclaimer} />)}
                </div>
                <div className="comparison-row">
                    <div className="pro-membership-table-title table-title-light">
                        <TableTooltip
                            title="Authorized For Pickup"
                            text="Designated individuals that can collect your winnings on your behalf. Only Pro membership accounts can have or be an Authorized Pickup Person."
                        />
                    </div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.authorizedForPickup} color="#ededed" />)}
                </div>
                <div className="comparison-row">
                    <div className="table-title-white table-title-light">
                        <TableTooltip title="Dedicated Email Support" />
                    </div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.dedicatedEmailSupport ? 'Yes' : 'No'} color="#F5F5F5" yesNo={true} />)}
                </div>
                <div className="comparison-row">
                    <div className="pro-membership-table-title table-title-light">
                        <TableTooltip title="Dedicated Phone Support" />
                    </div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.dedicatedPhoneSupport ? 'Yes' : 'No'} color="#ededed" yesNo={true} />)}
                </div>
                <div className="comparison-row">
                    <div className="table-title-white table-title-light">
                        <TableTooltip title="Suspension Review" text="Subject to the severity and number of incidents." />
                    </div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.suspensionReview ? 'Yes' : 'No'} color="#F5F5F5" yesNo={true} />)}
                </div>
                <div className="comparison-row">
                    <div className="pro-membership-table-title table-title-light">
                        <TableTooltip title="Backup Bidder Offers" text="When the originating winning bidder fails to complete the unit process, buyers gain eligibility to these offers." disclaimer="* based on auctions won" />
                    </div>
                    {memberships.map((membership, index) => <TableValue key={index} text={membership.backupOffers ? 'Yes' : 'No'} color="#ededed" yesNo={true} disclaimer={membership?.maxBackupBidderOffersDisclaimer} />)}
                </div>
                <div className="comparison-row marginT35 paddingB30">
                    <div className="pro-membership-table-title table-title-white">
                        <span></span>
                    </div>
                    {memberships.map((membership, index) => (
                        <div key={index} className="table-value white-space-pre">
                            <BtnLink
                                to={authed ? membership.buttonLink : loginLink({ redirect_to: '/pro-membership-upgrade' })}
                                label={membership.buttonText}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default MembershipComparison;
